/* eslint-disable @typescript-eslint/member-delimiter-style */
/* eslint-disable multiline-ternary */
/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { DownloadIcon } from "@chakra-ui/icons"
import {
  Box, Button, Flex, Spinner, Text, Tooltip, useToast, type ChakraProps
} from "@chakra-ui/react"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Paths } from "../../../common/commonConstants"
import { SnapPopoverNotes } from "../../../components/commons/popover"
import { Colors } from "../../../theme/colors"
import {
  useAddToFmpProjectMutation,
  useFilterProductLazyQuery,
  useGetFmpSystemsProductsLazyQuery,
  useGetReportLazyQuery,
  useRemoveFromFmpProjectMutation,
  type ProductFilterInput
} from "../../../type-and-hooks"
import { FMPDropDown, type SelectedFMPType } from "../addToFMPDropdown/addToFMPDropdown"
import { useProductFilterData } from "./producFilter.data.hook"
import { SelectButton } from "./selectButton"
// import { InfoOutlineIcon } from '@chakra-ui/icons'
import { PDFIcon } from "../../../components/productInfo/product-info.main"
import { type ApolloError } from "@apollo/client"
import { useSet } from "@uidotdev/usehooks"
import { useAtom, useSetAtom } from "jotai"
import useErrorToast from "../../../common/commonHooks/errorToast"
import { useReportError, verifyPdfAccessible } from "../../../common/commonHooks/useReportError"
import { buildDocumentUrl } from "../../../common/documentUrl"
import { userDetailsAtom } from "../../../components/Menu/menu.store"
import { useIsLoggedIn } from "../../../components/commons/utils"
import { extractValidObjects, hasMandatoryFields } from "../../../components/commons/utilsTs"
import useFetchFilteredFMP from "../common.fmp.hooks/fetchFilteredFMP.data.hook"
import { useGetHTMLforPDFDownload } from "../common.fmp.hooks/pdfDownload.hook"
import { pdfGeneratorAtom } from "../common.fmp.hooks/pdfGenerator.store"
import { applyBestMatchFilter } from "./productFilter.support.bestMatch"
import { specialTypeSwap } from "./productFilter.support"
import { FMPPagination } from "./productFilter.support.pagination"
import { productFilterFormDataAtom } from "./productFilter.support.store"
import { DEFAULT_RESULTS_VIEW_MODE, ResultsViewModeRadio, type ResultsViewMode } from "./productFilter.support.viewMode"

const tableStyleHeader: ChakraProps = {
  bg: Colors.Primary.accent,
  color: "white",
  fontWeight: "normal",
  textAlign: "left",
  alignContent: "center",
  py: 1,
  fontSize: "12px",
  fontFamily: "PT Sans",
  borderRight: `1px solid ${Colors.Secondary.c2_500}`
}
const nameColumnStyles: ChakraProps = {
  minW: "150px"
}

const columnStyles = {
  minH: "40px",
  bg: "white",
  p: "1",
  alignItems: "center",
  borderRight: `1px solid ${Colors.Secondary.c2_500}`,
  borderBottom: `1px solid ${Colors.Secondary.c2_500}`
}
const buttonProps = { fontSize: "12px", variant: "linkButton", fontWeight: "normal" }
const CENTERED_HEADERS = ["Add to FMP", "Element", "Application", "Product", "Service", "Nom Size", "Fitting Allowed", "Note", "FRL", "Report"]

export type selectedRowsType = Record<string, boolean>

interface FMPTableProps {
  activeProject: SelectedFMPType | null
  onDropdownChange: (value: SelectedFMPType) => void
  defaultFmpId?: number
}

const FMPTable = ({ activeProject, onDropdownChange, defaultFmpId }: FMPTableProps) => {
  const navigate = useNavigate()
  const activeFmpId = activeProject?.selectedFMP.id
  const navigateToEditFMP = (): void => {
    if (activeFmpId) {
      navigate(Paths.FMP.EDIT_FMP, { state: { fmpId: Number(activeFmpId) } })
    }
  }
  const [selectedRows, setSelectedRows] = useState<selectedRowsType>({})
  const tableRef = useRef<HTMLDivElement>(null)
  const { generate: generateHTMLForPDF } = useGetHTMLforPDFDownload(tableRef)
  const setPdfGenerator = useSetAtom(pdfGeneratorAtom)
  useEffect(() => {
    setPdfGenerator(() => generateHTMLForPDF)
    return () => {
      setPdfGenerator(null)
    }
  }, [generateHTMLForPDF, setPdfGenerator])
  const { filteredFMP, error: filteredFMPError, refetch } = useFetchFilteredFMP(activeProject?.selectedFMP.id)
  const [catchError, setCatchError] = useState<ApolloError | null>(null)
  const [addToFMPProject, { data: addingFMPToProjectSuccess, loading: loadingFMPToProject }] = useAddToFmpProjectMutation()
  const [removeToFMPProject, { data: removingFromFMPProject, loading: removingFMPProject }] = useRemoveFromFmpProjectMutation()
  const [userDetails] = useAtom(userDetailsAtom)
  const userId = useCallback(() => Number(userDetails?._id), [userDetails])
  const toast = useToast()
  const [productFilterFormData] = useAtom(productFilterFormDataAtom)
  const [filterProduct, { data: tableData, loading, error: filterProductError }] = useFilterProductLazyQuery()

  // "Systems" mode shows every system already assigned to the active FMP,
  // unfiltered, as an alternative to the criteria-filtered results table.
  const [isSystemsMode, setIsSystemsMode] = useState(false)
  const [fetchFmpSystems, { data: systemsData, loading: systemsLoading, error: systemsError }] = useGetFmpSystemsProductsLazyQuery()
  const assignedSystemIds = filteredFMP?.[0]?.systems ?? []
  const hasSystemsAssigned = assignedSystemIds.length > 0

  const handleToggleSystemsMode = (): void => {
    const nextIsSystemsMode = !isSystemsMode
    setIsSystemsMode(nextIsSystemsMode)
    setActivePage(0)
    if (nextIsSystemsMode && assignedSystemIds.length > 0) {
      fetchFmpSystems({ variables: { systemIds: assignedSystemIds.map(Number) } }).catch(() => {
        // Error handled by Apollo Client
      })
    }
  }

  const addToFMP = useSet()
  const removeFromFMP = useSet()
  const allActivelyClickedSystems = useSet()

  const actionsAfterAddAndRemoveCalls = () => {
    refetch()
    addToFMP.clear()
    removeFromFMP.clear()
    allActivelyClickedSystems.clear()
  }
  const isLoggedIn = useIsLoggedIn()
  // onclick of the main action button, depending on the state of the activeProject, it will either create a new FMP or add to the existing FMP.
  const handleMainAction = async () => {
    const systemIds = Object.keys(selectedRows)
    const userID = userId()
    if (!selectedRows || !userID || !systemIds.length) return
    const activeFMPID = activeProject?.selectedFMP?.id
    const systemsToRemove = Array.from(removeFromFMP) as number[]
    const systemsToadd = Array.from(addToFMP) as number[]

    if (activeFMPID && systemsToadd.length && userID) {
      await addToFMPProject({
        variables: {
          input: {
            fmp_project_id: activeFMPID,
            system_id: systemsToadd,
            user_id: userID
          }
        }
      }).catch(setCatchError)
    }
    if (systemsToRemove.length && activeFMPID && userID) {
      await removeToFMPProject({
        variables: {
          input: {
            fmp_project_id: activeFMPID,
            system_id: systemsToRemove,
            user_id: userID
          }
        }
      }).catch(setCatchError)
    }
    actionsAfterAddAndRemoveCalls()
  }

  const handleSelect = (systemId: string, selected: boolean) => {
    if (!isLoggedIn) {
      toast({
        title: "Error",
        description: "Please login to add systems to FMP",
        status: "error",
        duration: 5000,
        isClosable: true
      })
      return
    }
    const allValues = { ...selectedRows, [systemId]: selected }
    const filterOnlyValuesWithTrue = Object.keys(allValues).reduce((reducer, key) => {
      if (allValues[key]) {
        return { ...reducer, [key]: true }
      }
      return {
        ...reducer
      }
    }, {})
    const defaultState = getDefaultState(Number(systemId))
    if (selected) {
      allActivelyClickedSystems.add(systemId)
    } else {
      allActivelyClickedSystems.delete(systemId)
    }
    if (selected && defaultState) {
      // System already present in FMP List, move to systems to be removed
      removeFromFMP.add(systemId)
      addToFMP.delete(systemId)
    }
    if (selected && !defaultState) {
      // System is not already present in FMP List, move to systems to be added
      removeFromFMP.delete(systemId)
      addToFMP.add(systemId)
    }
    // user unselected. remove that system ID from the selected list of both add and remove
    if (!selected) {
      addToFMP.delete(systemId)
      removeFromFMP.delete(systemId)
    }
    setSelectedRows(filterOnlyValuesWithTrue)
  }

  const getDefaultState = useCallback(
    (system_id: number | undefined): boolean => {
      if (!system_id) return false
      const filteredFMPSystems = filteredFMP?.[0]?.systems ?? []

      return filteredFMPSystems.includes(system_id)
    },
    [filteredFMP]
  )

  useEffect(() => {
    if (addingFMPToProjectSuccess) {
      toast({
        title: "Success",
        description: "Successfully added to FMP",
        status: "success",
        duration: 2000,
        isClosable: true
      })
    }
  }, [addingFMPToProjectSuccess, toast])
  useEffect(() => {
    if (removingFromFMPProject) {
      toast({
        title: "Success",
        duration: 2000,
        description: "System successfully removed from FMP",
        status: "success",
        isClosable: true
      })
    }
  }, [removingFromFMPProject, toast])
  // Form DataAPI call
  const formChangeHandler = useCallback((): void => {
    const formValues = productFilterFormData
    const nullExcludedFormValues = extractValidObjects(formValues, true)
    const keys = Object.keys(nullExcludedFormValues) as Array<keyof ProductFilterInput>
    const specialTypes: ProductFilterInput = keys.reduce((accumulator, key) => {
      return {
        ...accumulator,
        [key]: specialTypeSwap<unknown>(nullExcludedFormValues[key] ?? "")
      }
    }, {})

    if (specialTypes && hasMandatoryFields(specialTypes)) {
      filterProduct({
        variables: {
          criteria: {
            ...specialTypes
          }
        }
      }).catch(() => {
        // Error handled by Apollo Client
      })
    }
  }, [productFilterFormData, filterProduct])

  const [viewMode, setViewMode] = useState<ResultsViewMode>(DEFAULT_RESULTS_VIEW_MODE)

  const rawResultsData = isSystemsMode ? systemsData?.getFmpSystemsProducts : tableData?.filterProduct
  const isTableLoading = isSystemsMode ? systemsLoading : loading
  const viewModeResultsData = viewMode === "best"
    ? applyBestMatchFilter(rawResultsData)
    : rawResultsData
  const { data } = useProductFilterData(viewModeResultsData)
  // Fetch FMP Error
  useErrorToast({ toast, error: filteredFMPError, errorMessage: "Error Fetching FMP" })
  // Adding to FMP Error
  useErrorToast({ toast, error: catchError, errorMessage: "Error adding to FMP" })
  // Fetching Table Error
  useErrorToast({ toast, error: filterProductError, errorMessage: "Error fetching table Data" })
  // Fetching Systems Error
  useErrorToast({ toast, error: systemsError, errorMessage: "Error fetching FMP systems" })

  // Reloading table data on form change
  useEffect(() => {
    formChangeHandler()
    setActivePage(0)
    setViewMode(DEFAULT_RESULTS_VIEW_MODE)
  }, [formChangeHandler])

  useEffect(() => {
    // If active project changes, clear the sets and reset selected rows.
    addToFMP.clear()
    removeFromFMP.clear()
    setActivePage(0) // Reset pagination to page 1 when switching projects
    setIsSystemsMode(false) // Fall back to the filtered view - the old systems list belongs to the previous project
    const allEntries = allActivelyClickedSystems.entries()
    const nextSelectedRows: selectedRowsType = {}
    if (filteredFMP?.length && filteredFMP[0]) {
      for (const [entry] of allEntries) {
        const defaultState = getDefaultState(Number(entry))
        if (defaultState) {
          // System Id already present in FMP, its selected so move it to remove list.
          removeFromFMP.add(entry)
        } else {
          addToFMP.add(entry)
        }
        // Keep selectedRows (drives the Update button) in sync with the
        // re-populated sets above - it was previously always cleared here,
        // so carried-over amber/green selections stayed visible on the new
        // project but the Update button stayed hidden.
        nextSelectedRows[entry as string] = true
      }
    }
    setSelectedRows(nextSelectedRows)
  }, [activeProject, filteredFMP]) // Remove unstable dependencies to prevent infinite loop

  const [activePage, setActivePage] = useState<number>(0)
  const [numberOfRowsPerPage, setNumberOfRowsPerPage] = useState<number>(10)
  const [downloadLoading, setDownloadLoading] = useState<string | null>(null)

  const handleProductClick = (productGenericCode: string) => {
    navigate(`/products/fire-collar/${encodeURIComponent(productGenericCode)}`)
  }

  const { reportError } = useReportError()
  // Add the GetReport lazy query hook
  const [getReport, { loading: reportLoading }] = useGetReportLazyQuery()

  const handleReportClick = async (report_name: string): Promise<void> => {
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const url = data?.getReport?.reportURL ?? null
      if (!url) throw new Error("Report URL not found")
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on product filter page` })
        return
      }
      window.open(buildDocumentUrl(data?.getReport?.fileName, url) ?? url, "_blank", "noopener,noreferrer")
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to load on product filter page` })
    }
  }

  const handleDirectDownload = async (report_name: string): Promise<void> => {
    setDownloadLoading(report_name)
    try {
      const { data } = await getReport({ variables: { reportNo: report_name } })
      const url = data?.getReport?.reportURL ?? null
      const name = data?.getReport?.reportName ?? report_name
      if (!url) throw new Error("Report URL not found")
      const downloadUrl = data?.getReport?.downloadURL ?? url
      const accessible = await verifyPdfAccessible(url)
      if (!accessible) {
        reportError({ context: "Inaccessible Report", detail: `Report "${report_name}" returned access denied on product filter page` })
        return
      }
      const link = document.createElement("a")
      link.href = downloadUrl
      link.download = `${name}.pdf`
      link.target = "_blank"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch {
      reportError({ context: "Missing Report", detail: `Report "${report_name}" failed to download on product filter page` })
    } finally {
      setDownloadLoading(null)
    }
  }

  return (
    <>
    <Box mt="0">
      <Flex gap="16px" alignItems="flex-end" justifyContent="space-between" pos={"relative"} w="100%" flexWrap="nowrap" overflowX="auto">
        <Flex gap="16px" alignItems="center" flexWrap="nowrap" flexShrink={0}>
          <Flex alignItems="center" gap="8px" pb="2" flexShrink={0}>
            <Text variant="RCaptionBold" color={Colors.Primary.c1} fontSize="16px" whiteSpace="nowrap">
              Project Name:
            </Text>
            <Box w={["100%", "260px"]}>
              <FMPDropDown onDropdownChange={onDropdownChange} loginNotRequired={true} defaultFmpId={defaultFmpId} />
            </Box>
            {activeFmpId && (
              <Button variant="Button1RegularWithShadow" whiteSpace="nowrap" onClick={navigateToEditFMP}>
                Edit Plan
              </Button>
            )}
            {activeFmpId && hasSystemsAssigned && (
              <Button
                variant={isSystemsMode ? "Button1RegularAccent" : "Button1Regular"}
                _hover={isSystemsMode ? { bg: "#8B6B5E" } : undefined}
                whiteSpace="nowrap"
                onClick={handleToggleSystemsMode}
              >
                {isSystemsMode ? "Filters" : "Systems"}
              </Button>
            )}
          </Flex>

          {rawResultsData && (
            <Flex justify="flex-start" gap="16px" alignItems="center" zIndex={101} position={"relative"} pb="2" flexShrink={0}>
              {Object.keys(selectedRows).length > 0 && (
                <Button
                  variant={"Button1RegularAmber"}
                  boxShadow="0px 3px 5px 0px rgba(0, 0, 0, 0.25)"
                  onClick={() => {
                    void handleMainAction()
                  }}
                  isLoading={loadingFMPToProject || removingFMPProject}
                  isDisabled={!activeProject}
                >
                  Update
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        <Box flexShrink={0}>
          <FMPPagination
            handleNumberOfRowsPerPage={setNumberOfRowsPerPage}
            data={data}
            numberOfRowsPerPage={numberOfRowsPerPage}
            activePage={activePage}
            setActivePage={setActivePage}
          />
        </Box>
      </Flex>

      {Boolean(rawResultsData) && !isSystemsMode && (
        <Flex justifyContent="flex-start" mb="2">
          <ResultsViewModeRadio value={viewMode} onChange={setViewMode} />
        </Flex>
      )}

      {/* Table */}
      {isTableLoading && (
        <Flex w="100%" m="4" align="center" justify={"center"}>
          <Spinner />
        </Flex>
      )}

      <Box>
        <Box
          ref={tableRef}
          overflow={"auto"}
          mt="0"
          height="520px"
          opacity={data ? 1 : 0}
          transition={"400ms"}
          minHeight="300px"
          minW="900px"
        >
          {data?.headers && !isTableLoading && (
            <>
              {data?.columns?.length === 0 ? (
                <Flex height="300px" width="100%" alignItems="center" justifyContent="center" bg="white">
                  <Text color="gray.500" fontSize="14px">
                    No records found
                  </Text>
                </Flex>
              ) : (
                <Box display={"grid"} gridTemplateColumns={"43px repeat(9, 1fr)"} fontSize={"12px"} textAlign={"center"}>
                  {data?.headers?.map((header, index) => (
                    <Box
                      zIndex={50}
                      position={"sticky"}
                      top={0}
                      key={index}
                      {...tableStyleHeader}
                      display="flex"
                      alignItems="center"
                      {...(CENTERED_HEADERS.includes(header) && { textAlign: "center", justifyContent: "center" })}
                      {...(header === "Add to FMP" && { bg: Colors.Primary.c1 })}
                      {...(index === 0 && { borderLeft: `1px solid ${Colors.Secondary.c2_500}` })}
                    >
                      <Box w="100%">{header}</Box>
                    </Box>
                  ))}
                  {/* Rows */}

                  {data?.columns?.slice(activePage * numberOfRowsPerPage, (activePage + 1) * numberOfRowsPerPage).map(({ values }) => {
                    const {
                      element_name,
                      application_name,
                      product_generic_code,
                      service_name,
                      nominal_size,
                      fitting_allowed,
                      note,
                      frl,
                      report_name,
                      report_publish_status,
                      system_id
                    } = values

                    return (
                      <React.Fragment key={`table-row-${system_id}`}>
                        <Box
                          {...columnStyles}
                          p="0"
                          borderLeft={`1px solid ${Colors.Secondary.c2_500}`}
                        >
                          <SelectButton
                            systemId={Number(system_id)}
                            isBlank={element_name === ""}
                            addToFMP={addToFMP}
                            removeFromFMP={removeFromFMP}
                            onClickCallBack={value => {
                              handleSelect(system_id, value)
                            }}
                            defaultState={getDefaultState(Number(system_id))}
                          />
                        </Box>
                        <Flex {...columnStyles} {...nameColumnStyles}>
                          <Box w="100%">{element_name}</Box>
                        </Flex>
                        <Flex {...columnStyles}>
                          <Box w="100%">{application_name}</Box>
                        </Flex>
                        <Flex {...columnStyles}>
                          <Box w="100%">
                            <Button
                              {...buttonProps}
                              onClick={() => {
                                handleProductClick(product_generic_code)
                              }}
                            >
                              {product_generic_code}
                            </Button>
                          </Box>
                        </Flex>
                        {/* <Flex {...columnStyles} borderBottom={isLastRow ? 'none' : `1px solid ${Colors.Secondary.c2_500}`}>
                          <Box w="100%">{service_type}</Box>
                        </Flex> */}
                        <Flex {...columnStyles}>
                          <Box w="100%">{service_name}</Box>
                        </Flex>
                        <Flex {...columnStyles}>
                          <Box w="100%">{nominal_size}</Box>
                        </Flex>
                        <Flex {...columnStyles}>
                          <Box w="100%">{fitting_allowed || "—"}</Box>
                        </Flex>
                        <Flex {...columnStyles} justifyContent="center">
                          {note &&
                            (() => {
                              try {
                                // Handle both string and array cases
                                const notesData = typeof note === "string" ? JSON.parse(note) : note

                                return (
                                  <Flex gap="0.25" flexWrap="wrap" alignItems="center">
                                    {notesData.map((item: { note_id: number; notes: string }, index: number) => (
                                      <React.Fragment key={item.note_id}>
                                        <SnapPopoverNotes text={item.notes} triggerText={item.note_id.toString()} noteIcon={undefined} />
                                        {index < notesData.length - 1 && <Text as="span">,</Text>}
                                      </React.Fragment>
                                    ))}
                                  </Flex>
                                )
                              } catch (error) {
                                // Fallback to original behavior if parsing fails
                                return <SnapPopoverNotes text={note} triggerText="Note" noteIcon={undefined} />
                              }
                            })()}
                        </Flex>
                        <Flex {...columnStyles}>
                          <Box w="100%">{frl}</Box>
                        </Flex>
                        {report_publish_status === "Public" && (
                          <Flex {...columnStyles}>
                            <Box w="100%">
                              <Flex align="center" justifyContent="center" gap="4px">
                                <Button
                                  variant="linkButton"
                                  p="0"
                                  minW="0"
                                  h="auto"
                                  display="flex"
                                  alignItems="center"
                                  flexShrink={0}
                                  onClick={() => { void handleDirectDownload(report_name) }}
                                  isLoading={downloadLoading === report_name}
                                  aria-label={`Download ${report_name}`}
                                >
                                  <DownloadIcon fontSize="12px" />
                                </Button>
                                <Button
                                  variant="linkButton"
                                  fontWeight="normal"
                                  fontSize="12px"
                                  display="flex"
                                  alignItems="center"
                                  gap="4px"
                                  onClick={() => { void handleReportClick(report_name) }}
                                  isLoading={reportLoading}
                                  disabled={reportLoading}
                                >
                                  <PDFIcon />
                                  {report_name}
                                </Button>
                              </Flex>
                            </Box>
                          </Flex>
                        )}
                        {report_publish_status !== "Public" && (
                          <Flex {...columnStyles}>
                            <Box w="100%">
                              <Flex align="center" justifyContent="center" gap="4px">
                                <Box p="0" minW="0" h="auto" visibility="hidden" aria-hidden="true">
                                  <DownloadIcon fontSize="12px" />
                                </Box>
                                <Tooltip label="* Please contact our customer support for the report." hasArrow placement="top">
                                  <Flex align="center" gap="4px" cursor="default">
                                    <PDFIcon />
                                    <Text as="span" fontWeight="normal">
                                      {report_name}
                                      {report_name ? <sup>*</sup> : ""}
                                    </Text>
                                  </Flex>
                                </Tooltip>
                              </Flex>
                            </Box>
                          </Flex>
                        )}
                      </React.Fragment>
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
        {Boolean(data?.columns?.length) && (
          <Text fontSize="12px" color={Colors.Secondary.c2} mt="2">
            * Please contact our customer support for the report.
          </Text>
        )}
      </Box>
    </Box>
    </>
  )
}

export default FMPTable
